/**
 * comparePanel.js — режим сравнения двух исследований.
 *
 * Задача врача: положить рядом старое и новое исследование одного пациента и
 * листать их вместе, чтобы увидеть, что изменилось. Это называется «смотреть
 * в динамике», и без него половина лучевой диагностики теряет смысл: один
 * снимок показывает, что есть, и не показывает, стало ли лучше.
 *
 * [10.9.0] Вход переделан. Была строка «Сравнение» в левой панели с двумя
 * «местами». Врач не смог её найти и спросил, как войти в режим сравнения, —
 * это и есть приговор той выдумке. Три её беды:
 *
 *  1. Раздел лежал в свёрнутой строке внутри панели, которая сама скрыта.
 *  2. «Место 1» в сайдбаре и левое окно на экране — две разные вещи, и связь
 *     между ними приходилось держать в голове.
 *  3. Панель на узком экране скрыта целиком, поэтому сравнение оказалось
 *     настольным — не по существу, а из-за того, куда я его положил.
 *
 * Теперь: кнопка «Режим сравнения» в верхнем ряду. По нажатию — два окна, и
 * выбор каталога стоит ПРЯМО В ТОМ ОКНЕ, где появится картинка. На узком
 * экране окна становятся друг над другом: две узкие полоски рядом смотреть
 * нечего.
 *
 * Реформатов здесь нет намеренно. Для динамики нужны обычные срезы, а два
 * объёма в памяти — удвоенный расход там, где он ничего не добавляет.
 */

import {
  getWorkerSeries, getWorkerSeriesImageIds, hasWorkerData,
  getStudyOfSeries, setKeepStudies, getMaxStudies,
} from '../loaders/loaderWorkerBridge.js';
import { getAllSeries, getSeriesImageIds } from '../loaders/loaderManager.js';
import { buildStudyIndex, formatStudyTitle, formatStudyBadge, sortStudiesByDate } from '../core/studyIndex.js';
import { setCompareActive, isCompareActive, setComparePair } from '../core/compareState.js';
import { getRenderingEngine, getViewportIds, loadStack } from '../core/viewport.js';
import {
  enableCompareSync, disableCompareSync,
  alignCompareHere, clearCompareAlignment, getCompareAlignment, mapComparePoint,
  mapComparePointInfo, setCompareAlignmentFromPair,
} from '../core/viewportSync.js';
import { changeLayout } from './layoutManager.js';
import { updateOverlays } from './seriesPanel.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { compareLesion, equivalentDiameter } from '../core/lesionVolume.js';
import { thinningWarning, thinningSnapshot } from '../core/loadThinning.js';
import { studyProtocolSummary, showProtocolReport } from './protocolCheck.js';
import { compareForFollowUp, выбратьСериюПоУмолчанию } from './protocolCompare.js';
import { уточнитьДеформацией, снятьДеформацию, деформацияАктивна, сводкаДеформации } from '../registration/deformCompare.js';   // [12.0.3]
import { открытьRECIST } from './recistPanel.js';   // [12.0.3]
import { открытьПрошлыеPACS, отправитьВАрхив } from './pacsCompareUI.js';   // [12.0.4]
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

/** Порог, ниже которого два окна ставим друг над другом, а не рядом. */
const УЗКИЙ_ЭКРАН = 768;

let _окна = [];                    // vpId в порядке слева направо / сверху вниз
const _вОкне = new Map();          // vpId → { studyUID, seriesUID }
let _окноЖдёт = null;              // куда положить следующее загруженное исследование
let _режимСвязи = 'none';

// ── Данные ───────────────────────────────────────────────────────────

/**
 * Серии из того загрузчика, который сейчас работает. Повторяет логику
 * uiManager._getAllSeries намеренно: тащить сюда весь uiManager ради двух
 * строк — значит завязать модуль на четыре тысячи строк чужого кода.
 */
function _всеСерии() {
  if (hasWorkerData()) return getWorkerSeries();
  const s = getAllSeries();
  return Array.isArray(s) ? s : Object.values(s || {});
}

function _кадрыСерии(seriesUID) {
  const ids = getWorkerSeriesImageIds(seriesUID);
  if (ids?.length) return ids;
  return getSeriesImageIds(seriesUID) || [];
}

const _исследования = () => sortStudiesByDate(buildStudyIndex(_всеСерии()));
const _кадров = (s) => (s?.images?.length || s?.files?.length || 0);

function _серииИсследования(studyUID) {
  if (!studyUID) return [];
  return _всеСерии().filter((s) => {
    const uid = getStudyOfSeries(s.seriesUID) || s.studyUID || 'unknown_study';
    return uid === studyUID;
  }).filter((s) => _кадров(s) > 0);
}

/** Серия по умолчанию — самая длинная ИЗ РАБОЧИХ: скауты и производные в
 * умолчание не годятся. [12.0.8] По снимку владельца: в окне оказался
 * обзорник в 15 кадров с толщиной 5 мм, и весь вердикт сопоставимости честно
 * описывал ЕГО — «5 мм», «без контраста». Правило отсева — чистое,
 * выбратьСериюПоУмолчанию (protocolCompare), проверено тестом; сводка серий
 * требует тегов и потому async. Запасной путь прежний — длиннейшая. */
async function _сериюПоУмолчанию(studyUID) {
  const список = _серииИсследования(studyUID);
  if (!список.length) return '';
  try {
    const с = await studyProtocolSummary(studyUID);
    const свои = (с?.series || []).filter((x) => список.some((л) => л.seriesUID === x.uid));
    const uid = выбратьСериюПоУмолчанию(свои);
    if (uid) return uid;
  } catch (e) { log.debug('[Сравнение] умолчание по сводке:', e.message); }
  return список.reduce((a, b) => (_кадров(b) > _кадров(a) ? b : a)).seriesUID;
}

const _ВЗВЕШ = {
  t1: 'T1', t2: 'T2', flair: 'FLAIR', dwi: 'DWI', mra: 'MRA/MRV',
  ssfp: 'FIESTA/CISS', swi: 'SWI/T2*', scout: 'скаут', other: 'не распознана',
};

/**
 * [10.19.1] Подобрать в это окно серию, ПАРНУЮ той, что уже лежит в другом.
 *
 * Зачем. Раньше каждое окно независимо брало самую длинную серию своего
 * исследования. В июле самой длинной могла оказаться 3D T1 BRAVO, в августе —
 * AX T2 FLAIR, и врач получал рядом две разные съёмки. Совместить их
 * программа как раз сможет: совмещение считает взаимную информацию (MI), она
 * для этого и придумана — этим же кодом совмещаются КТ с МРТ. А вот всё, что
 * идёт ПОСЛЕ совмещения, на такой паре бессмысленно: карта разницы вычитает
 * разные физические величины, а объём очага мерится по разным границам —
 * контрастное ядро против отёка. Строка сопоставимости об этом честно
 * говорила, но исправлять приходилось руками, каждый раз.
 *
 * Теперь пара подбирается сама: та же взвешенность, тот же контраст, та же
 * плоскость, ближайшая толщина среза; при равенстве — более длинная серия.
 * Если парной серии в исследовании нет вовсе — берём ближайшую и говорим об
 * этом вслух, а не молча.
 */
async function _подобратьСерию(studyUID, образец) {
  const свои = _серииИсследования(studyUID);
  if (!свои.length) return '';
  const поДлине = () => _сериюПоУмолчанию(studyUID);   // async — все выходы ниже await-ятся вызывающим
  if (!образец?.studyUID || !образец?.seriesUID || образец.studyUID === studyUID) return поДлине();
  try {
    const [мой, чужой] = await Promise.all([
      studyProtocolSummary(studyUID),
      studyProtocolSummary(образец.studyUID),
    ]);
    const эталон = (чужой?.series || []).find((s) => s.uid === образец.seriesUID);
    if (!эталон) return поДлине();
    const мои = (мой?.series || []).filter((s) => свои.some((x) => x.seriesUID === s.uid));
    const годные = мои.filter((s) => !s.isLocalizer && !s.isDerived);
    const кандидаты = годные.length ? годные : мои;
    if (!кандидаты.length) return поДлине();

    const балл = (s) => {
      let б = 0;
      if (s.kind && эталон.kind && s.kind === эталон.kind) б += 100;
      if (s.hasContrast === эталон.hasContrast) б += 50;
      if (s.plane && эталон.plane && s.plane === эталон.plane) б += 20;
      if (Number.isFinite(s.thk) && Number.isFinite(эталон.thk)) {
        б += Math.max(0, 10 - Math.abs(s.thk - эталон.thk) * 4);
      }
      б += Math.min(5, (s.count || 0) / 200);   // при прочих равных — длиннее
      return б;
    };
    const лучший = кандидаты.slice().sort((a, b) => балл(b) - балл(a))[0];
    if (!лучший?.uid) return поДлине();

    if (лучший.kind !== эталон.kind || лучший.hasContrast !== эталон.hasContrast) {
      const нужно = `${_ВЗВЕШ[эталон.kind] || эталон.kind}${эталон.hasContrast ? ' с контрастом' : ' без контраста'}`;
      showToast(`В этом исследовании нет пары к съёмке из другого окна (${нужно}). `
        + `Взял «${(лучший.desc || '').slice(0, 30) || _ВЗВЕШ[лучший.kind] || 'ближайшую'}»: совместить их можно, `
        + 'но объём очага и карту разницы на разной взвешенности считать нельзя', 'warning', 9000);
    }
    return лучший.uid;
  } catch (e) {
    log.debug('[Сравнение] подбор пары:', e.message);
    return поДлине();
  }
}

/** Исследования, которые ещё никуда не положены. */
function _свободные() {
  const занятые = new Set([..._вОкне.values()].map((v) => v.studyUID).filter(Boolean));
  return _исследования().filter((ст) => !занятые.has(ст.studyUID));
}

// ── Разметка внутри окна ─────────────────────────────────────────────

function _элементОкна(vpId) {
  try { return getRenderingEngine()?.getViewport(vpId)?.element || null; }
  catch (e) { return null; }
}

/** Убрать всё, что мы нарисовали поверх окон. */
function _очиститьОкна() {
  document.querySelectorAll('.cmp-pick, .cmp-badge, .cmp-info, .cmp-cursor').forEach((el) => el.remove());
  document.getElementById('cmp-align-bar')?.remove();
  _снятьУказатель();
}

// ── [10.12.0] Указатель между окнами ─────────────────────────────────
//
// Врач написал: «не работает перекрестие». Перекрестие Cornerstone не сломано —
// оно неприменимо: построено для объёмных окон и ходит по трём плоскостям
// ОДНОГО объёма, а в сравнении оба окна плоские стопки из РАЗНЫХ исследований.
// Для стопок оно в программе намеренно отключено, иначе падает на каждом
// движении мыши.
//
// Поэтому здесь своё, простое и честное: ведёте мышью в одном окне — во втором
// появляется отметка в том же месте тела. Пересчёт берётся тот же, каким
// связана прокрутка: ручная поправка или перенос от совмещения. Одно правило
// на обе вещи — иначе отметка покажет одно, а листание уведёт в другое.

const _снятияУказателя = [];

function _снятьУказатель() {
  while (_снятияУказателя.length) {
    try { _снятияУказателя.pop()(); } catch (e) { /* */ }
  }
}

function _отметка(vpId) {
  const el = _элементОкна(vpId);
  if (!el) return null;
  let m = el.querySelector('.cmp-cursor');
  if (!m) {
    m = document.createElement('div');
    m.className = 'cmp-cursor';
    m.innerHTML = '<i></i><i></i>';
    el.appendChild(m);
  }
  return m;
}

/**
 * Показать во втором окне точку, на которую врач навёл в первом.
 * Возвращает координаты отметки — их же читает проверка.
 */
function _показатьУказатель(откуда, страница) {
  const re = getRenderingEngine();
  const vpОт = re?.getViewport(откуда);
  const холст = vpОт?.element?.querySelector('canvas');
  if (!vpОт || !холст) return null;

  const r = холст.getBoundingClientRect();
  let точка = null;
  try { точка = vpОт.canvasToWorld([страница.x - r.left, страница.y - r.top]); }
  catch (e) { return null; }
  if (!точка) return null;

  let итог = null;
  for (const куда of _окна) {
    if (куда === откуда) continue;
    const m = _отметка(куда);
    const vpК = re?.getViewport(куда);
    const холст2 = vpК?.element?.querySelector('canvas');
    if (!m || !vpК || !холст2) continue;
    const перенесённая = mapComparePoint(точка, откуда, куда) || точка;
    let экран = null;
    try { экран = vpК.worldToCanvas(перенесённая); } catch (e) { экран = null; }
    if (!экран) { m.style.display = 'none'; continue; }
    // Точка может лежать ВНЕ кадра второй съёмки: охваты у двух сессий разные.
    // Тогда отметку не показываем вовсе. Отметка у самого края означала бы
    // «вот здесь», хотя правильный ответ — «этого места на второй съёмке нет».
    const r2 = холст2.getBoundingClientRect();
    // [10.19.1] Сверяем с CSS-размером холста: worldToCanvas отвечает в CSS-
    // пикселях, а холст2.width — device-пиксели; на DPR>1 сравнение с ними
    // показывало отметку у края там, где этого места на второй съёмке нет.
    if (экран[0] < 0 || экран[1] < 0 || экран[0] > r2.width || экран[1] > r2.height) {
      m.style.display = 'none';
      итог = { vpId: куда, x: экран[0], y: экран[1], внеКадра: true };
      continue;
    }
    const rЭл = vpК.element.getBoundingClientRect();
    m.style.left = `${(r2.left - rЭл.left) + экран[0]}px`;
    m.style.top = `${(r2.top - rЭл.top) + экран[1]}px`;
    m.style.display = 'block';
    итог = { vpId: куда, x: экран[0], y: экран[1] };
  }
  return итог;
}

function _спрятатьУказатель() {
  document.querySelectorAll('.cmp-cursor').forEach((m) => { m.style.display = 'none'; });
}

/** Навести указатель по всем окнам сравнения. */
function _включитьУказатель() {
  _снятьУказатель();
  for (const vpId of _окна) {
    const el = _элементОкна(vpId);
    if (!el) continue;
    const ход = (e) => { _показатьУказатель(vpId, { x: e.clientX, y: e.clientY }); };
    const нажатие = (e) => {
      if (!_ждёмТочкуОчага) return;
      // [10.19.1] Клик по элементам управления — НЕ точка очага. Раньше
      // capture-перехват съедал клик по списку серий, кнопке ✕ или вердикту
      // качества и измерял случайную точку под ними, а сам элемент глох.
      if (e.target?.closest?.('button, select, input, a, .cmp-info, .cmp-pick, .cmp-qa, .cmp-align-bar')) return;
      e.stopPropagation(); e.preventDefault();
      _измеритьОчаг(vpId, { x: e.clientX, y: e.clientY }).catch((err) => log.warn('[Очаг]', err.message));
    };
    const уход = () => _спрятатьУказатель();
    el.addEventListener('mousemove', ход);
    el.addEventListener('mouseleave', уход);
    el.addEventListener('mousedown', нажатие, true);
    _снятияУказателя.push(() => {
      el.removeEventListener('mousemove', ход);
      el.removeEventListener('mouseleave', уход);
      el.removeEventListener('mousedown', нажатие, true);
    });
  }
  // [10.19.1] Из режима «укажите очаг» должен быть выход: Esc отменяет.
  // Раньше режим нельзя было отменить ничем.
  const клавиша = (e) => {
    if (e.key === 'Escape' && _ждёмТочкуОчага) {
      _ждёмТочкуОчага = false;
      showToast('Указание очага отменено', 'info', 2000);
      _состояниеПривязки();
    }
  };
  document.addEventListener('keydown', клавиша);
  _снятияУказателя.push(() => document.removeEventListener('keydown', клавиша));
}

/**
 * [10.11.0] Полоска привязки между окнами.
 *
 * Здесь живёт то, что относится к ПАРЕ, а не к отдельному окну: сказать «эти
 * два среза — один уровень» и снять сказанное. Поэтому она не в окне, а между
 * ними: действие парное, и место у него парное.
 */
function _полоскаПривязки() {
  const дом = document.getElementById('viewport-container');
  if (!дом) return;
  let бар = document.getElementById('cmp-align-bar');
  const заполненныеОкна = _окна.filter((id) => _вОкне.get(id)?.seriesUID);
  const заполнены = заполненныеОкна.length >= 2;

  // [11.1.9] Полоска БОЛЬШЕ НЕ ИСЧЕЗАЕТ МОЛЧА.
  //
  // Владелец сообщил: два исследования загружены, а окна совмещения между
  // окнами нет. Прежде здесь стояло `if (!заполнены) { бар?.remove(); return; }`
  // — полоска просто убиралась, ничего не объясняя. Снаружи «нет окна» и
  // «программа считает, что окна не заполнены» выглядят одинаково, и найти
  // причину нельзя ни врачу, ни мне: я потратил на догадки несколько заходов.
  //
  // Теперь при нехватке полоска остаётся и говорит, ЧЕГО ей не хватает, а в
  // журнал уходит точное состояние обоих мест. Это тот же урок, что со связкой
  // окон: молчание неотличимо от поломки.
  log.info(`[Сравнение] полоска: окон ${_окна.length}, с сериями ${заполненныеОкна.length}`
    + ` · ${_окна.map((id) => `${id}=${_вОкне.get(id)?.seriesUID ? 'серия есть' : (_вОкне.get(id)?.studyUID ? 'только исследование' : 'пусто')}`).join(', ')}`);

  if (!заполнены) {
    if (!бар) {
      бар = document.createElement('div');
      бар.id = 'cmp-align-bar';
      дом.appendChild(бар);
    }
    const чего = _окна.length < 2
      ? 'нет двух окон сравнения'
      : `в ${2 - заполненныеОкна.length} окне из двух нет серии — выберите исследование в пустом месте`;
    бар.innerHTML = `<span style="font-size:12px;color:#9fb3cc;padding:2px 4px">Совмещение недоступно: ${чего}</span>`;
    return;
  }

  // Полоска могла остаться с подсказкой — тогда её содержимое надо построить заново.
  if (бар && !бар.querySelector('.cmp-align-btn')) { бар.remove(); бар = null; }

  if (!бар) {
    бар = document.createElement('div');
    бар.id = 'cmp-align-bar';
    бар.innerHTML = `
      <button class="cmp-align-btn" data-act="align" title="Прокрутите оба окна на один и тот же ориентир и нажмите — дальше они пойдут вместе">Связать здесь</button>
      <button class="cmp-align-btn" data-act="autolevel" title="Программа сама найдёт одинаковый анатомический уровень по рельефу плотности срезов и свяжет окна на нём. Поправить всегда можно «Связать здесь»">Связать по уровню</button>
      <button class="cmp-align-btn" data-act="reset" title="Вернуться к исходным координатам">Сброс</button>
      <button class="cmp-align-btn" data-act="measure" title="Нажмите, затем укажите очаг в любом окне — программа измерит его в ОБОИХ исследованиях">Измерить очаг</button>
      <button class="cmp-align-btn" data-act="diff" title="Вычесть одно исследование из другого: красным — что прибавилось, синим — что убыло. Лучше после совмещения">Карта разницы</button>
      <button class="cmp-align-btn" data-act="deform" title="Деформируемое уточнение (дыхание, наполнение, изгиб): демоны на грубой сетке. Для НАВИГАЦИИ и переноса точек — изображения не искривляет. Лучше после «Совместить»">Деформация</button>
      <button class="cmp-align-btn" data-act="recist" title="Динамика целевых очагов по RECIST 1.1: таблица по датам, суммы, ответ CR/PR/SD/PD">RECIST</button>
      <button class="cmp-align-btn" data-act="priors" title="Прошлые исследования ЭТОГО пациента с подключённого PACS (QIDO по PatientID): выбрать и загрузить во второе окно">Прошлые с PACS</button>
      <button class="cmp-align-btn" data-act="store" title="Отправить исходные файлы серии на PACS (STOW-RS). Ответ разбирается на принято/отвергнуто">В архив</button>
      <span id="cmp-align-state"></span>
      <div id="cmp-compat" class="cmp-compat"></div>`;
    бар.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      if (btn.dataset.act === 'align') {
        // Порядок важен: включение связи сбрасывает поправки, поэтому сначала
        // включаем, потом запоминаем. Наоборот — и привязка сотрётся сразу.
        const { mode } = enableCompareSync(_окна);
        const r = alignCompareHere(_окна);
        if (!r.ok) {
          disableCompareSync();
          showToast(`Связать не вышло: ${r.why}`, 'warning', 3500);
        } else {
          _режимСвязи = mode;
          showToast(`Связано здесь: смещение ${r.мм.toFixed(1)} мм. Дальше окна идут вместе`, 'success', 3500);
        }
      } else if (btn.dataset.act === 'autolevel') {
        // [12.0.11] Подбор уровня грузит и разбирает десятки срезов обоих
        // исследований — секунды. Кнопка честно занята, как соседние.
        btn.disabled = true;
        const прежний = btn.textContent;
        btn.textContent = 'ищу уровень…';
        _итогИзмерения = 'ищу одинаковый анатомический уровень…';
        _состояниеПривязки();
        _связатьПоУровню(_окна)
          .then((r) => {
            if (!r.ok) {
              _итогИзмерения = `уровень не найден: ${r.why}`;
              showToast(`Связать по уровню не вышло: ${r.why}`, 'warning', 4500);
            } else {
              _режимСвязи = r.mode;
              _итогИзмерения = `связано по уровню: смещение ${r.мм.toFixed(1)} мм, сходство рельефов ${Math.round(r.корреляция * 100)}%`;
              showToast(`Связано по уровню: смещение ${r.мм.toFixed(1)} мм (сходство ${Math.round(r.корреляция * 100)}%). Поправить — «Связать здесь»`, 'success', 5000);
            }
          })
          .catch((e) => {
            _итогИзмерения = `уровень не найден: ${e.message}`;
            showToast(`Связать по уровню не вышло: ${e.message}`, 'warning', 4500);
          })
          .finally(() => { btn.disabled = false; btn.textContent = прежний; _состояниеПривязки(); });
      } else if (btn.dataset.act === 'diff') {
        // Считается это секундами и требует обоих томов, поэтому кнопка сразу
        // говорит, что занята: молчащая кнопка читается как сломанная.
        btn.disabled = true;
        const прежний = btn.textContent;
        btn.textContent = 'считаю…';
        _итогИзмерения = 'строю карту разницы…';
        _состояниеПривязки();
        import('../registration/differenceOverlay.js')
          .then((m) => m.toggleDifference())
          .then((r) => {
            _итогИзмерения = r.ok
              ? (r.включена ? `карта разницы: ${r.словами}` : '')
              : `карта разницы не вышла: ${r.why}`;
            btn.textContent = (r.ok && r.включена) ? 'Убрать карту' : прежний;
          })
          .catch((e) => { _итогИзмерения = `карта разницы не вышла: ${e.message}`; btn.textContent = прежний; })
          .finally(() => { btn.disabled = false; _состояниеПривязки(); });
      } else if (btn.dataset.act === 'deform') {
        // [12.0.3] Деформируемое уточнение. Считается секундами (демоны на
        // грубой сетке), поэтому кнопка честно занята на время счёта.
        if (деформацияАктивна()) {
          снятьДеформацию();
          btn.textContent = 'Деформация';
          _итогИзмерения = 'деформируемый перенос снят';
          _состояниеПривязки();
        } else {
          btn.disabled = true;
          const прежний = btn.textContent;
          btn.textContent = 'считаю…';
          _итогИзмерения = 'считаю деформацию (демоны, грубая сетка)…';
          _состояниеПривязки();
          уточнитьДеформацией()
            .then((r) => {
              if (!r.ok) {
                _итогИзмерения = `деформация не вышла: ${r.причина}`;
                btn.textContent = прежний;
                showToast(_итогИзмерения, 'warning', 8000);
                return;
              }
              const с = r.сводка;
              _итогИзмерения = `деформация: среднее ${с.среднееМм.toFixed(1)} мм, макс ${с.максМм.toFixed(1)} мм`
                + (r.жёсткоеУчтено ? ' · поверх жёсткого совмещения' : ' · БЕЗ жёсткого — поле включает укладку');
              btn.textContent = 'Снять деформацию';
              showToast(`Деформируемый перенос включён (${_итогИзмерения}). Для навигации и переноса точек; изображения не искривляются`,
                'success', 9000);
            })
            .catch((e) => { _итогИзмерения = `деформация не вышла: ${e.message}`; btn.textContent = прежний; })
            .finally(() => { btn.disabled = false; _состояниеПривязки(); });
        }
      } else if (btn.dataset.act === 'recist') {
        // [12.0.3] Окно динамики. Пациент и даты — из индекса исследований.
        try {
          const список = _исследования();
          const пациент = список.find((ст) => ст.patientId) || список[0] || {};
          открытьRECIST({
            patientId: пациент.patientId || '',
            patientName: пациент.patientName || '',
            даты: список.map((ст) => ({ дата: String(ст.studyDate || '').replace(/\D/g, '').slice(0, 8), подпись: formatStudyTitle(ст) })),
            последняяМера: () => _мераДляRECIST,
          });
        } catch (e) { showToast('RECIST: ' + e.message, 'error', 6000); }
      } else if (btn.dataset.act === 'priors') {
        // [12.0.4] Прошлые этого пациента с PACS — в свободное окно.
        try {
          const список = _исследования();
          const пациент = список.find((ст) => ст.patientId) || {};
          открытьПрошлыеPACS({
            patientId: пациент.patientId || '',
            patientName: пациент.patientName || '',
            загруженныеUID: new Set(список.map((ст) => ст.studyUID)),
            послеЗагрузки: () => onStudiesChanged(),
          });
        } catch (e) { showToast('Прошлые с PACS: ' + e.message, 'error', 6000); }
      } else if (btn.dataset.act === 'store') {
        // [12.0.4] В архив: серии из окон сравнения, исходными файлами.
        try {
          отправитьВАрхив({
            окна: _окна.map((vpId, i) => {
              const з = _вОкне.get(vpId) || {};
              return { seriesUID: з.seriesUID, studyUID: з.studyUID, подпись: `окно ${i + 1}: ${_названиеИсследования(з.studyUID) || 'пусто'}` };
            }),
          });
        } catch (e) { showToast('В архив: ' + e.message, 'error', 6000); }
      } else if (btn.dataset.act === 'measure') {
        // [10.19.1] Повторное нажатие отменяет ожидание точки (плюс Esc).
        _ждёмТочкуОчага = !_ждёмТочкуОчага;
        showToast(_ждёмТочкуОчага
          ? 'Укажите очаг в любом окне — измерю его в обоих исследованиях. Esc — отмена'
          : 'Указание очага отменено', 'info', _ждёмТочкуОчага ? 5000 : 2000);
        _состояниеПривязки();
      } else {
        disableCompareSync();
        _режимСвязи = 'none';
        showToast('Связь снята — окна листаются по отдельности', 'info', 2500);
      }
      _состояниеПривязки();
    });
    дом.appendChild(бар);
  }
  _состояниеПривязки();
}

/**
 * [11.1.10] ПОЛОСКА ПЕРЕЖИВАЕТ ПЕРЕСБОРКУ ОКОН.
 *
 * ВОТ ГДЕ ОНА ПРОПАДАЛА. Полоска — прямой ребёнок `#viewport-container`
 * (иначе не сработает правило стиля `#viewport-container > #cmp-align-bar`).
 * А `setupViewports` при каждой перестройке делает `container.innerHTML = ''`
 * (viewport.js:384) и сметает ВСЁ содержимое контейнера, полоску в том числе.
 *
 * Обычно она строится после раскладки и выживает. Но загрузка серии в окно
 * может дёрнуть пересборку ещё раз — уже ПОСЛЕ того, как полоска встала. Тогда
 * её стирает, и заново никто не строит: `_полоскаПривязки` зовётся только из
 * `_обновитьВсё`, а тот к этому моменту отработал. Снаружи это выглядит как
 * «панель совмещения исчезла» — ровно то, о чём говорил владелец.
 *
 * Наблюдатель возвращает полоску, как только контейнер перестроили. Следит
 * только за прямыми детьми, чтобы не срабатывать на каждую мелочь внутри окон.
 */
let _сторожПолоски = null;
function _стеречьПолоску() {
  if (_сторожПолоски) return;
  const дом = document.getElementById('viewport-container');
  if (!дом) return;
  _сторожПолоски = new MutationObserver(() => {
    if (!isCompareActive()) return;
    if (document.getElementById('cmp-align-bar')) return;   // на месте — не трогаем
    log.info('[Сравнение] полоску смело пересборкой окон — возвращаю');
    _полоскаПривязки();
  });
  _сторожПолоски.observe(дом, { childList: true });
}

function _снятьСторожПолоски() {
  if (_сторожПолоски) { _сторожПолоски.disconnect(); _сторожПолоски = null; }
}

/**
 * [10.14.0] Измерение очага на две даты.
 *
 * Врач спрашивал про модели поиска опухолей. Для его случая полезнее другое:
 * он ЗНАЕТ, где очаг, а вопрос — изменился ли. Значит нужно не искать, а
 * измерить одно и то же одинаково дважды. Указанная точка переносится во
 * второе исследование тем же правилом, каким связаны окна, и очаг меряется
 * там же. Способ объяснимый — рост области по коридору яркости, — и врач
 * видит, что именно посчитано.
 */
let _ждёмТочкуОчага = false;
let _мераДляRECIST = null;   // [12.0.3] { записи:[{дата,мм}], откуда } — окно RECIST заберёт кнопкой
let _итогИзмерения = '';

/**
 * [10.19.1] Опции измерения. Уточнение семени по самому яркому вокселю (3 мм)
 * для тёмного, не копящего контраст очага вредно — теперь его можно выключить,
 * не трогая код: localStorage «mikhmel.уточнятьСемяМм» = 0 (или своё расстояние
 * в мм). Снаружи то же делает MikhmelViewer.compareSeedRefine(мм).
 */
function _опцииОчага() {
  try {
    const с = Number(localStorage.getItem('mikhmel.уточнятьСемяМм'));
    return Number.isFinite(с) ? { уточнитьПоЯркому: с } : {};
  } catch (e) { return {}; }
}

async function _измеритьОчаг(vpId, страница) {
  _ждёмТочкуОчага = false;
  const re = getRenderingEngine();
  const vp = re?.getViewport(vpId);
  const холст = vp?.element?.querySelector('canvas');
  if (!vp || !холст) return;

  const r = холст.getBoundingClientRect();
  let точка = null;
  try { точка = vp.canvasToWorld([страница.x - r.left, страница.y - r.top]); }
  catch (e) { точка = null; }
  if (!точка) { showToast('Не удалось определить точку', 'warning', 3000); return; }

  _итогИзмерения = 'считаю…';
  _состояниеПривязки();
  showToast('Считаю объём очага в обоих исследованиях…', 'info', 4000);

  try {
    const рег = await import('../registration/rtRegisterModule.js');
    const мера = async (окно, мир) => {
      const с = _вОкне.get(окно)?.seriesUID;
      if (!с) return null;
      return рег.measureLesionInSeries?.(с, мир, _опцииОчага());
    };

    const итоги = [];
    const способы = new Map();
    for (const окно of _окна) {
      let мир = точка;
      if (окно !== vpId) {
        const п = mapComparePointInfo(точка, vpId, окно);
        мир = п.мир || точка;
        способы.set(окно, п.способ);
        if (п.способ === 'тождество') {
          // [10.19.1] Молчаливое тождество между РАЗНЫМИ исследованиями — обман:
          // без привязки и совмещения «та же точка» — это координаты стола,
          // промах на укладку в сантиметры, а уточнение по яркости услужливо
          // найдёт рядом чужую яркую структуру и вернёт правдоподобное число
          // не о том месте. Внутри одного исследования тождество законно.
          const своё = _вОкне.get(vpId)?.studyUID, чужое = _вОкне.get(окно)?.studyUID;
          if (своё && чужое && своё !== чужое) {
            итоги.push({ окно, r: { ok: false,
              why: 'окна не связаны — нажмите «Связать здесь» или выполните совмещение, иначе точка попадёт не туда' } });
            continue;
          }
        }
      }
      итоги.push({ окно, r: await мера(окно, мир) });
    }

    const годные = итоги.filter((x) => x.r?.ok);
    if (годные.length < 2) {
      _итогИзмерения = `измерить не вышло: ${итоги.map((x) => x.r?.why).filter(Boolean).join('; ') || 'нет данных'}`;
    } else {
      // [10.19.1] «Было → стало» — по ДАТАМ исследований, а не по порядку
      // окон: врач волен положить новое исследование слева, и раньше рост
      // молча читался бы как убыль.
      let [a, b] = годные;
      try {
        const список = _исследования(); // отсортированы от НОВОГО к старому
        const номер = (окно) => {
          const uid = _вОкне.get(окно)?.studyUID;
          const i = список.findIndex((ст) => ст.studyUID === uid);
          return i < 0 ? null : i;
        };
        const na = номер(a.окно), nb = номер(b.окно);
        if (na != null && nb != null && na < nb) [a, b] = [b, a];
      } catch (e) { /* запасной порядок — как лежат окна */ }
      const с = compareLesion(a.r.объёмМм3, b.r.объёмМм3);
      const дA = equivalentDiameter(a.r.объёмМм3), дB = equivalentDiameter(b.r.объёмМм3);
      // [12.0.3] Отдаём меру окну RECIST в структурированном виде: дата
      // исследования каждого окна + эквивалентный диаметр. Порядок к этому
      // месту уже выправлен по датам (см. выше), a — старое, b — новое.
      try {
        const датаОкна = (окно) => {
          const uid = _вОкне.get(окно)?.studyUID;
          const ст = _исследования().find((x) => x.studyUID === uid);
          return String(ст?.studyDate || '').replace(/\D/g, '').slice(0, 8);
        };
        const зап = [
          { дата: датаОкна(a.окно), мм: дA },
          { дата: датаОкна(b.окно), мм: дB },
        ].filter((з) => /^\d{8}$/.test(з.дата));
        _мераДляRECIST = зап.length === 2 ? { записи: зап, откуда: 'Измерить очаг, эквивалентный диаметр по объёму' } : null;
      } catch (e) { _мераДляRECIST = null; }
      _итогИзмерения = `очаг: ${с.словами} · диаметр ${дA.toFixed(1)} → ${дB.toFixed(1)} мм`;
      const сп = [...способы.values()].find((s) => s && s !== 'та же точка');
      if (сп === 'привязка') _итогИзмерения += ' · перенос: ручная привязка (вдоль оси, без поперечного сдвига)';
      else if (сп === 'совмещение') _итогИзмерения += ' · перенос: по совмещению';
      if (a.r.упёрлись || b.r.упёрлись) _итогИзмерения += ' · рост упёрся в предел — проверьте точку';
      // [10.19.1] Число, посчитанное на прореженной серии, обязано сказать об
      // этом само: тост в момент загрузки через месяц никто не вспомнит.
      const прорежено = thinningWarning(итоги.map((x) => _вОкне.get(x.окно)?.seriesUID).filter(Boolean));
      if (прорежено) _итогИзмерения += ` · ${прорежено}`;
      showToast(_итогИзмерения, 'success', 9000);
      log.info(`[Очаг] ${_итогИзмерения}`);
    }
  } catch (e) {
    _итогИзмерения = `измерить не вышло: ${e.message}`;
  }
  _состояниеПривязки();
}

/** Что показала последняя мера — наружу для проверок. */
export const getLesionResult = () => _итогИзмерения;

function _состояниеПривязки() {
  const el = document.getElementById('cmp-align-state');
  if (!el) return;
  if (_ждёмТочкуОчага) { el.textContent = 'укажите очаг в окне'; return; }
  if (_итогИзмерения) { el.textContent = _итогИзмерения; return; }
  const п = getCompareAlignment();
  if (п.поСовмещению) { el.textContent = 'связано по совмещению'; return; }
  const пары = Object.values(п.сдвиг);
  el.textContent = пары.length
    ? `связано вручную: ${пары[0].toFixed(1)} мм`
    : 'не связаны — листаются по отдельности';
}

/**
 * [10.10.0] Сведения о пациенте и съёмке — В КАЖДОМ окне.
 *
 * Врач написал: «в левой панели полная информация, в правой только дата».
 * Причина не в сравнении: блок сведений в программе один, и заполняется он по
 * той серии, которая считается текущей. Какое окно текущее — в то и пишется.
 *
 * Здесь блок КЛОНИРУЕТСЯ в каждое окно, а заполняет его та же самая функция
 * updateOverlays — ей теперь можно указать корень. Идентификаторы у копии
 * снимаются: два элемента с одним id — это следующая тихая ошибка, которую
 * потом ищут полдня. Поиск идёт по пометке data-ov, она у копии остаётся.
 */
function _сведенияВОкно(vpId) {
  const el = _элементОкна(vpId);
  const зап = _вОкне.get(vpId);
  if (!el) return;
  el.querySelectorAll('.cmp-info').forEach((x) => x.remove());
  if (!зап?.seriesUID) return;

  const образец = document.querySelector('.vp-overlay-tl');
  if (!образец) return;
  const копия = образец.cloneNode(true);
  копия.removeAttribute('id');
  копия.classList.add('cmp-info');
  копия.querySelectorAll('[id]').forEach((x) => x.removeAttribute('id'));
  // Мобильный значок видеокарты и подобное к сведениям о съёмке не относятся.
  копия.querySelectorAll('[data-ov="tool"]').forEach((x) => x.remove());
  el.appendChild(копия);

  try { updateOverlays(зап.seriesUID, копия); }
  catch (e) { log.debug('[Сравнение] сведения:', e.message); }
}

/**
 * Подпись исследования наверху окна: «1. МРТ 12.03.2024».
 * Без неё через минуту работы уже не помнишь, где старое, а где новое, — и
 * это самая опасная ошибка в динамике.
 */
function _подписьОкна(vpId, номер) {
  const занято = _вОкне.get(vpId);
  const el = _элементОкна(vpId);
  if (!el) return;
  el.querySelectorAll('.cmp-badge').forEach((b) => b.remove());
  if (!занято?.studyUID) return;
  const текст = formatStudyBadge(buildStudyIndex(_всеСерии()).get(занято.studyUID));
  if (!текст) return;
  const badge = document.createElement('div');
  badge.className = 'cmp-badge';
  badge.textContent = `${номер}. ${текст}`;
  el.appendChild(badge);
}

/**
 * Нарисовать в окне поле выбора или строку выбранного исследования.
 *
 * Всё содержимое — обычные элементы поверх холста. Нажатия внутри гасим,
 * иначе Cornerstone примет их за работу инструментом: нажатие на кнопку
 * начинало бы тянуть яркость.
 */
function _нарисоватьОкно(vpId, номер) {
  const el = _элементОкна(vpId);
  if (!el) return;
  el.querySelectorAll('.cmp-pick').forEach((p) => p.remove());

  const занято = _вОкне.get(vpId);
  const блок = document.createElement('div');
  блок.className = 'cmp-pick' + (занято?.studyUID ? ' cmp-pick-filled' : '');
  блок.dataset.vp = vpId;

  if (!занято?.studyUID) {
    const свободные = _свободные();
    const готовые = свободные.length
      ? `<div class="cmp-pick-ready">
           <div class="cmp-pick-sub">Уже загружено — можно взять сразу:</div>
           ${свободные.map((ст) => `<button class="cmp-pick-btn" data-study="${ст.studyUID}">${formatStudyTitle(ст)}</button>`).join('')}
         </div>`
      : '';
    блок.innerHTML = `
      <div class="cmp-pick-title">Исследование ${номер}</div>
      <div class="cmp-pick-sub">Выберите каталог — оно откроется в этом окне</div>
      <div class="cmp-pick-row">
        <button class="cmp-pick-btn primary" data-act="folder">Открыть папку</button>
        <button class="cmp-pick-btn" data-act="archive">Из архива</button>
      </div>
      ${готовые}`;
  } else {
    const серии = _серииИсследования(занято.studyUID);
    const название = formatStudyTitle(buildStudyIndex(_всеСерии()).get(занято.studyUID));
    блок.innerHTML = `
      <div class="cmp-pick-line">
        <span class="cmp-pick-name" title="${название}">${название}</span>
        <button class="cmp-pick-btn small" data-act="clear" title="Освободить окно">✕</button>
      </div>
      <select class="cmp-pick-series" aria-label="Серия в окне ${номер}">
        ${серии.map((s) => `<option value="${s.seriesUID}"${s.seriesUID === занято.seriesUID ? ' selected' : ''}>${[s.modality, s.seriesDesc, `${_кадров(s)} срез.`].filter(Boolean).join(' • ')}</option>`).join('')}
      </select>`;
  }

  блок.addEventListener('mousedown', (e) => e.stopPropagation());
  блок.addEventListener('pointerdown', (e) => e.stopPropagation());
  блок.addEventListener('wheel', (e) => e.stopPropagation());
  блок.addEventListener('click', (e) => {
    e.stopPropagation();
    const btn = e.target.closest('button');
    if (!btn) return;
    const act = btn.dataset.act;
    if (act === 'folder') { _окноЖдёт = vpId; document.getElementById('btn-open-folder')?.click(); return; }
    if (act === 'archive') { _окноЖдёт = vpId; document.getElementById('btn-archive')?.click(); return; }
    if (act === 'clear') { _вОкне.delete(vpId); _обновитьВсё(); return; }
    if (btn.dataset.study) _положить(vpId, btn.dataset.study).catch((e2) => log.warn('[Сравнение]', e2.message));
  });
  блок.addEventListener('change', (e) => {
    e.stopPropagation();
    if (!e.target.classList.contains('cmp-pick-series')) return;
    const зап = _вОкне.get(vpId);
    if (!зап) return;
    // [12.0.8] ПОКАЗ — ТРАНЗАКЦИЯ. Раньше seriesUID переписывался ДО показа,
    // и при неудаче показа (кадры не собрались) список под окном говорил одно,
    // а окно показывало другое — ровно рассинхрон со снимка владельца: в
    // списке «3D BRAVO · 158 срез.», в окне обзорник 15 кадров. Теперь при
    // неудаче выбор откатывается, окно перерисовывается, причина — тостом.
    const прежняя = зап.seriesUID;
    зап.seriesUID = e.target.value;
    зап.выборРучной = true;   // руками выбранное авто-подбор не трогает никогда
    _показать(vpId).then((вышло) => {
      if (!вышло) {
        зап.seriesUID = прежняя;
        showToast('Серия не открылась — оставил прежнюю', 'error', 6000);
      }
      _обновитьВсё();
    });
  });

  el.appendChild(блок);
  _подписьОкна(vpId, номер);
  _сведенияВОкно(vpId);
}

/**
 * [10.17.0] Вердикт качества — В САМОМ ОКНЕ, по серии ЭТОГО окна.
 *
 * Та же беда, что была в 10.10.0 со сведениями о пациенте, и лечится так же.
 * Раньше проверка качества была одна на программу: считалась по «текущей»
 * серии и вываливалась баннером во всю ширину экрана. При двух исследованиях
 * это негодно трижды — вердикт один на два, непонятно, к какому относится, и
 * баннер закрывает сами окна.
 *
 * Хуже того, сама сверка брала ВСЕ загруженные серии разом и закрывала
 * требования июля августовскими сериями (см. protocol_study_test). Поэтому
 * здесь спрашивается строго про своё исследование.
 */
// Расчёт качества идёт по тегам и может занять секунды. За это время
// _обновитьВсё успевает пройти второй раз — и в окне оказывались ДВА вердикта
// друг под другом. Видно это было только на снимке экрана: обе надписи верные,
// просто их две. Поэтому у каждого окна свой номер захода, и дорисовывает
// только последний.
const _заходКачества = new Map();

async function _качествоВОкно(vpId) {
  const el = _элементОкна(vpId);
  const зап = _вОкне.get(vpId);
  if (!el) return null;
  const мойЗаход = (_заходКачества.get(vpId) || 0) + 1;
  _заходКачества.set(vpId, мойЗаход);
  el.querySelectorAll('.cmp-qa').forEach((x) => x.remove());
  if (!зап?.studyUID) return null;

  let с = null;
  try { с = await studyProtocolSummary(зап.studyUID); }
  catch (e) { log.debug('[Сравнение] качество:', e.message); return null; }
  // Пока считали, могли позвать ещё раз — тогда рисует тот, кто пришёл позже.
  if (_заходКачества.get(vpId) !== мойЗаход) return с;
  if (!с || !el.isConnected) return с;
  // Промежуточная чистка. САМА ПО СЕБЕ гонку не закрывает: ниже ещё два await
  // (import + evaluateSeries) — окончательная проверка стоит у appendChild.
  el.querySelectorAll('.cmp-qa').forEach((x) => x.remove());

  // Субмиллиметровый шлюз — вторая строка того же вердикта. В сравнении общий
  // баннер молчит (он один на два окна и закрывает снимки), поэтому его вывод
  // должен быть здесь, иначе врач просто потеряет эти сведения.
  let шлюз = null;
  try {
    const м = await import('../io/rtProtocolGateModule.js');
    шлюз = await м.evaluateSeries?.(зап.seriesUID);
  } catch (e) { log.debug('[Сравнение] шлюз:', e.message); }

  const цвет = с.уровень === 'ok' ? 'rgba(80,200,120,.85)'
    : с.уровень === 'warn' ? 'rgba(255,190,90,.9)' : 'rgba(255,120,110,.92)';
  const узел = document.createElement('div');
  узел.className = 'cmp-qa';
  узел.style.color = цвет;
  узел.title = 'Протокол-контроль этого исследования — нажмите для полного разбора';
  узел.textContent = `${с.значок} ${с.словами}`;
  if (шлюз?.res) {
    const р = шлюз.res;
    const строка = document.createElement('div');
    строка.style.cssText = 'margin-top:3px;font-weight:500;opacity:.92;';
    строка.style.color = р.level === 'ok' ? 'rgba(120,225,160,.95)'
      : р.level === 'warn' ? 'rgba(255,200,110,.95)' : 'rgba(255,140,130,1)';
    строка.textContent = `${р.level === 'ok' ? '✅' : (р.level === 'warn' ? '⚠️' : '⛔')} `
      + `срез ${р.throughMm} мм · воксель ${р.trueInPlaneMm} мм`
      + (р.interpolated ? ' · разрешение интерполировано' : '');
    узел.appendChild(строка);
  }
  // Нажатие открывает разбор ИМЕННО ЭТОГО исследования: иначе врач получит
  // отчёт про соседнее и не заметит подмены.
  узел.addEventListener('mousedown', (e) => e.stopPropagation());
  узел.addEventListener('click', (e) => {
    e.stopPropagation();
    showProtocolReport({ studyUID: зап.studyUID, title: _названиеИсследования(зап.studyUID) });
  });
  // [10.19.1] ПОСЛЕДНЯЯ проверка захода — прямо перед вставкой. Прежняя
  // «повторная чистка у самой вставки» стояла ДО двух await выше и гонку не
  // закрывала: чужой заход успевал дорисовать, и в окне оказывались два
  // одинаковых вердикта — поймано на снимке экрана при аудите.
  if (_заходКачества.get(vpId) !== мойЗаход || !el.isConnected) return с;
  el.querySelectorAll('.cmp-qa').forEach((x) => x.remove());
  // Кладём ВНУТРЬ блока сведений — врач просил «рядом со сведениями о съёмке».
  // Заодно это снимает вопрос о наложениях: узел течёт под текстом, а не
  // прибит к своему углу, где рано или поздно встретится с чужим элементом.
  (el.querySelector('.cmp-info') || el).appendChild(узел);
  return с;
}

function _названиеИсследования(studyUID) {
  try { return formatStudyTitle(buildStudyIndex(_всеСерии()).get(studyUID)) || ''; }
  catch (e) { return ''; }
}

/**
 * [10.17.0] Сопоставимы ли две съёмки МЕЖДУ СОБОЙ.
 *
 * Отдельно от качества каждой, потому что это разные вопросы. Обе съёмки
 * бывают безупречными по своему протоколу и при этом несравнимыми: июль снят
 * срезом 1 мм, август — 3 мм. Тогда часть найденного роста будет от толщины
 * среза, а не от опухоли, и объём очага с картой разницы соврут оба.
 */
async function _сопоставимость() {
  const строка = document.getElementById('cmp-compat');
  if (!строка) return null;
  const заняты = _окна.filter((id) => _вОкне.get(id)?.seriesUID);
  if (заняты.length < 2) { строка.textContent = ''; строка.className = 'cmp-compat'; return null; }

  let разборы = [];
  try {
    for (const id of заняты.slice(0, 2)) {
      const зап = _вОкне.get(id);
      const с = await studyProtocolSummary(зап.studyUID);
      разборы.push((с.series || []).find((x) => x.uid === зап.seriesUID) || null);
    }
  } catch (e) { log.debug('[Сравнение] сопоставимость:', e.message); return null; }

  // [12.0.8] Стороны в предупреждении — окнами, как их видит врач.
  const в = compareForFollowUp(разборы[0], разборы[1], { имяА: 'в левом окне', имяБ: 'в правом окне' });
  строка.textContent = в.словами;
  строка.className = 'cmp-compat cmp-compat-' + в.уровень;
  return в;
}

function _обновитьВсё() {
  _окна.forEach((vpId, i) => _нарисоватьОкно(vpId, i + 1));
  _связать();
  _полоскаПривязки();
  // Качество считается по тегам и требует подгрузки — не держим на нём отрисовку.
  Promise.all(_окна.map((id) => _качествоВОкно(id)))
    .then(() => _сопоставимость())
    .catch((e) => log.debug('[Сравнение] качество:', e.message));
  _переподобратьПару().then((б) => б && undefined).catch((e) => log.debug('[Сравнение] пере-подбор:', e.message));
  if (_окна.filter((id) => _вОкне.get(id)?.seriesUID).length >= 2) _включитьУказатель();
  else _снятьУказатель();
}

// ── Показ ────────────────────────────────────────────────────────────

async function _показать(vpId) {
  const зап = _вОкне.get(vpId);
  if (!зап?.seriesUID) return false;
  const кадры = _кадрыСерии(зап.seriesUID);
  if (!кадры.length) {
    showToast('У этой серии не осталось кадров — загрузите исследование заново', 'error', 4000);
    return false;
  }
  try { await loadStack(vpId, кадры); return true; }
  catch (e) { log.warn('[Сравнение] окно не открылось:', e.message); return false; }
}

/** Положить исследование в окно и открыть его. */
async function _положить(vpId, studyUID) {
  // [10.19.1] Пара подбирается по тому, что уже стоит в другом окне.
  const образец = [..._вОкне.entries()]
    .filter(([id]) => id !== vpId)
    .map(([, з]) => з)
    .find((з) => з?.studyUID && з?.seriesUID) || null;
  const seriesUID = await _подобратьСерию(studyUID, образец);
  _вОкне.set(vpId, { studyUID, seriesUID, выборРучной: false });
  _окноЖдёт = null;
  // [12.0.8] Показ — транзакция: не открылось — окно остаётся пустым с
  // причиной, а не «в списке одно, на экране другое».
  const вышло = await _показать(vpId);
  if (!вышло) {
    _вОкне.delete(vpId);
    showToast('Исследование не открылось в окне сравнения — выберите серию вручную или загрузите заново', 'error', 7000);
  }
  _обновитьВсё();
}

/**
 * Подготовить пару, когда оба окна заполнены.
 *
 * [10.11.0] Связь здесь БОЛЬШЕ НЕ ВКЛЮЧАЕТСЯ сама, и это не упрощение, а
 * исправление. Пока окна связаны, развести их на один и тот же ориентир
 * невозможно: двигаешь одно — второе тут же уезжает следом. А связывать по
 * сырым координатам двух разных сессий нельзя: они отсчитываются от стола
 * томографа, а не от анатомии, и «тот же уровень» у них не совпадает.
 *
 * Поэтому: сначала окна листаются по отдельности, врач ставит их на общий
 * ориентир и нажимает «Связать здесь» — тогда и включается связь, уже с
 * правильной поправкой. Либо связь включает совмещение, посчитав перенос само.
 */
// [12.0.8] ПАРА ПОДБИРАЕТСЯ ПРИ ЛЮБОМ ПОРЯДКЕ ЗАПОЛНЕНИЯ. Раньше похожую
// серию искали только для ВТОРОГО положенного исследования: первое стояло на
// умолчании, и если врач заполнял окна в «неудачном» порядке, рядом
// оказывались непарные съёмки. Теперь, когда заполнились оба окна, для
// каждого АВТО-выбранного (не руками!) прогоняется тот же подбор по образцу
// из соседнего окна; руками выбранная серия не трогается никогда.
let _переподборИдёт = false;
async function _переподобратьПару() {
  if (_переподборИдёт) return;
  const заполненные = _окна.filter((id) => _вОкне.get(id)?.seriesUID);
  if (заполненные.length < 2) return;
  _переподборИдёт = true;
  try {
    for (const vpId of заполненные) {
      const зап = _вОкне.get(vpId);
      if (!зап || зап.выборРучной) continue;
      const образец = заполненные.filter((id) => id !== vpId)
        .map((id) => _вОкне.get(id)).find((з) => з?.studyUID && з?.seriesUID) || null;
      if (!образец) continue;
      const лучшая = await _подобратьСерию(зап.studyUID, образец);
      if (!лучшая || лучшая === зап.seriesUID) continue;
      const прежняя = зап.seriesUID;
      зап.seriesUID = лучшая;
      const вышло = await _показать(vpId);
      if (!вышло) { зап.seriesUID = прежняя; continue; }
      const имя = _серииИсследования(зап.studyUID).find((s) => s.seriesUID === лучшая);
      showToast(`Пара подобрана: в окно поставлена «${[имя?.modality, имя?.seriesDesc].filter(Boolean).join(' ') || 'парная серия'}» (${_кадров(имя || {})} срез.). Не та — выберите руками в списке под окном`, 'info', 8000);
      _окна.forEach((id, i) => _нарисоватьОкно(id, i + 1));
    }
  } finally { _переподборИдёт = false; }
}

function _связать() {
  const заполненные = _окна.filter((id) => _вОкне.get(id)?.seriesUID);
  if (заполненные.length < 2) {
    disableCompareSync();
    _режимСвязи = 'none';
    setComparePair(null);
    return;
  }
  const [a, b] = заполненные;
  const зA = _вОкне.get(a), зB = _вОкне.get(b);
  setKeepStudies([зA.studyUID, зB.studyUID]);

  // Пара для совмещения: первое окно опорное, второе подвижное.
  const индекс = buildStudyIndex(_всеСерии());
  setComparePair({
    fixedSeriesUID: зA.seriesUID, movingSeriesUID: зB.seriesUID,
    fixedIds: _кадрыСерии(зA.seriesUID), movingIds: _кадрыСерии(зB.seriesUID),
    fixedLabel: formatStudyBadge(индекс.get(зA.studyUID)),
    movingLabel: formatStudyBadge(индекс.get(зB.studyUID)),
  });
  import('../registration/rtRegisterModule.js')
    .then((м) => м.refreshRegisterPair?.())
    .catch(() => { /* панели совмещения может не быть — не беда */ });

  showToast('Окна листаются по отдельности. Поставьте их на один ориентир и нажмите «Связать здесь»',
    'info', 5000);
}

// ── Вход и выход ─────────────────────────────────────────────────────

/**
 * [12.0.11] «Связать по уровню»: подобрать одинаковый анатомический уровень
 * двух окон по рельефу плотности срезов и привязать окна на нём.
 *
 * Порядок внутри — тот же, что у «Связать здесь», и по той же причине:
 * включение связи сбрасывает поправки, поэтому сначала enableCompareSync,
 * потом привязка. Сама привязка ставится не голым сдвигом, а от НАСТОЯЩЕЙ
 * пары срезов найденного уровня (setCompareAlignmentFromPair): обе поправки
 * считаются из геометрии обеих съёмок, без допущения «нормали совпадают».
 * Порог сходства 0.55: ниже него профили совпали случайно, и молча связать
 * означало бы уверенно показать врачу разные органы.
 */
async function _связатьПоУровню(окна) {
  const re = getRenderingEngine();
  const [a, b] = окна || [];
  const кадрыА = re?.getViewport?.(a)?.getImageIds?.() || [];
  const кадрыБ = re?.getViewport?.(b)?.getImageIds?.() || [];
  if (кадрыА.length < 5 || кадрыБ.length < 5) {
    return { ok: false, why: 'в обоих окнах нужны серии хотя бы по пять срезов' };
  }

  const [{ построитьПрофиль, лучшийСдвигМм }, { planeNormal }, { cornerstone }] = await Promise.all([
    import('../registration/sliceLevelMatch.js'),
    import('../core/sliceDepth.js'),
    import('../core/init.js'),
  ]);

  const загрузчик = async (imageId) => {
    const im = await cornerstone.imageLoader.loadAndCacheImage(imageId);
    const pm = cornerstone.metaData.get('imagePlaneModule', imageId);
    const ipp = pm?.imagePositionPatient, iop = pm?.imageOrientationPatient;
    if (!Array.isArray(ipp) || !Array.isArray(iop)) return null;
    const n = planeNormal(iop.map(Number));
    if (!n) return null;
    return {
      пиксели: im.getPixelData(),
      наклон: im.slope ?? 1,
      сдвиг: im.intercept ?? 0,
      позицияМм: ipp[0] * n[0] + ipp[1] * n[1] + ipp[2] * n[2],
    };
  };

  const профА = await построитьПрофиль(кадрыА, загрузчик);
  const профБ = await построитьПрофиль(кадрыБ, загрузчик);
  if (профА.значения.length < 5 || профБ.значения.length < 5) {
    return { ok: false, why: 'у срезов нет геометрии или пикселей для профиля' };
  }

  const итог = лучшийСдвигМм(профА, профБ);
  if (!итог.ok) return итог;
  if (итог.корреляция < 0.55) {
    return { ok: false, why: `рельефы совпали слабо (${Math.round(итог.корреляция * 100)}%) — свяжите вручную на видимом ориентире` };
  }

  // Пара срезов из середины перекрытия: уровень z в А отвечает z+сдвиг в Б
  // (условленность направления — в sliceLevelMatch).
  const минА = Math.min(...профА.позиции), максА = Math.max(...профА.позиции);
  const минБ = Math.min(...профБ.позиции), максБ = Math.max(...профБ.позиции);
  const от = Math.max(минА, минБ - итог.сдвигМм);
  const до = Math.min(максА, максБ - итог.сдвигМм);
  const центр = (от + до) / 2;
  const ближайший = (проф, z) => {
    let лучш = 0;
    for (let i = 1; i < проф.позиции.length; i++) {
      if (Math.abs(проф.позиции[i] - z) < Math.abs(проф.позиции[лучш] - z)) лучш = i;
    }
    return проф.кадры[лучш];
  };
  const кадрА = ближайший(профА, центр);
  const кадрБ = ближайший(профБ, центр + итог.сдвигМм);

  const { mode } = enableCompareSync(окна);
  const привязка = setCompareAlignmentFromPair(a, b, кадрА, кадрБ);
  if (!привязка.ok) { disableCompareSync(); return привязка; }
  return { ok: true, мм: привязка.мм, корреляция: итог.корреляция, перекрытиеМм: итог.перекрытиеМм, mode };
}

/** Рядом или друг над другом — по ширине экрана. */
function _раскладкаСравнения() {
  const ш = (typeof window !== 'undefined') ? window.innerWidth : 1200;
  return ш <= УЗКИЙ_ЭКРАН ? '2x1' : '1x2';
}

export async function enterCompareMode() {
  if (isCompareActive()) { await exitCompareMode(); return false; }

  // Флаг ставим ДО смены раскладки: changeLayout сам подгружает текущую серию
  // во все окна и показывает подсказку про пустые окна — здесь и то и другое
  // мешает.
  setCompareActive(true, []);
  await changeLayout(_раскладкаСравнения());

  _окна = getViewportIds();
  if (_окна.length < 2) {
    setCompareActive(false);
    showToast('Не удалось открыть два окна', 'error', 3000);
    return false;
  }
  setCompareActive(true, _окна);
  _вОкне.clear();
  _окноЖдёт = null;

  // Если что-то уже открыто — кладём это в первое окно сразу. Обычный случай
  // «смотрю новое, хочу подтянуть старое» тогда стоит одного нажатия.
  // Слева — то, что раньше, справа — то, что позже. В динамике читают по
  // времени слева направо: «было» и «стало». Список исследований отсортирован
  // свежими вперёд — для выбора это удобно, для расстановки надо наоборот.
  const готовые = [..._исследования()].reverse();
  if (готовые.length) await _положить(_окна[0], готовые[0].studyUID);
  if (готовые.length > 1) await _положить(_окна[1], готовые[1].studyUID);

  // Баннер субмиллиметрового шлюза мог всплыть ДО входа в сравнение — при
  // загрузке. Молчание в _banner() его уже не уберёт: он нарисован. Снимаем
  // руками, иначе он так и висит поперёк обоих окон, закрывая снимки, и
  // говорит про одно исследование из двух.
  document.getElementById('subm-banner')?.remove();
  document.getElementById('subm-chip')?.remove();

  // [11.1.7] И стартовую заставку. Она лежит ПОВЕРХ окон (absolute, inset 0,
  // z-index 10) и снимается только по факту появления снимков. Войти в
  // сравнение с пустой программой — значит получить два окна под заставкой:
  // в журнале «режим включён», на экране ничего. Именно это и наблюдал
  // владелец.
  import('./premiumFeatures.js')
    .then((м) => м.снятьЗаставку?.())
    .catch((e) => log.debug('[Сравнение] заставка:', e.message));

  _обновитьВсё();
  // [11.1.10] Ставим сторожа ПОСЛЕ первой отрисовки: дальше любую пересборку
  // окон он переживёт и вернёт полоску.
  _стеречьПолоску();
  _отметитьКнопку(true);
  log.info(`[Сравнение] режим включён, раскладка ${_раскладкаСравнения()}`);
  return true;
}

export async function exitCompareMode() {
  disableCompareSync();
  // [11.1.10] Снимаем сторожа полоски: вне сравнения возвращать нечего, а
  // наблюдатель, оставленный висеть, дёргался бы на каждой смене раскладки.
  _снятьСторожПолоски();
  // Полотно карты лежит НАД окном и движку не подчиняется — значит и убрать
  // его должны мы. Иначе оно останется висеть поверх обычной работы, и врач
  // будет смотреть на позапрошлую разницу, не понимая, откуда цвет.
  import('../registration/differenceOverlay.js')
    .then((m) => m.clearDifference())
    .catch(() => { /* модуль мог не загрузиться — это не повод падать */ });
  _очиститьОкна();
  _вОкне.clear();
  _окна = [];
  _окноЖдёт = null;
  _режимСвязи = 'none';
  setCompareActive(false);
  setKeepStudies([]);
  setComparePair(null);
  _отметитьКнопку(false);
  await changeLayout('1x1');
  showToast('Режим сравнения выключен', 'info', 1500);
}

function _отметитьКнопку(вкл) {
  try {
    document.getElementById('btn-compare-mode')?.classList.toggle('active', !!вкл);
    // [10.10.0] Общий блок сведений на время сравнения прячем.
    //
    // Он лежит в левом верхнем углу ВСЕГО поля окон, то есть поверх первого
    // окна, — и после того, как у каждого окна появился свой блок, в первом
    // окне оказалось два текста один поверх другого. Увидел на снимке экрана:
    // строки наложились и стали нечитаемыми. По разметке этого не видно, оба
    // блока «на своих местах».
    document.body.classList.toggle('compare-mode', !!вкл);
  } catch (e) { /* */ }
}

// ── Связь с загрузкой ────────────────────────────────────────────────

/**
 * Позвать после каждой загрузки: набор исследований изменился.
 *
 * Если окно ждало каталог — кладём в него то, что пришло. Узнать «то, что
 * пришло» можно только так: исследование, которого ещё нет ни в одном окне.
 * Прямого сигнала «загрузилось вот это» у загрузчика нет, а выдумывать его
 * ради одного места значило бы переделывать пол-загрузчика.
 */
/**
 * [10.19.0] Врач выбрал каталог, а потом отказался от прореживания.
 *
 * Раньше здесь не происходило ничего: окно оставалось ждать каталог, который
 * уже не придёт, и карточка выглядела так же, как до нажатия. Врач видел
 * «просто не проявляется» и не мог узнать причину ниоткуда.
 */
export function onOpenCancelled() {
  if (!isCompareActive()) return;
  const ждало = _окноЖдёт;
  _окноЖдёт = null;
  if (!ждало) return;
  const el = _элементОкна(ждало);
  const карточка = el?.querySelector('.cmp-pick');
  if (карточка) {
    карточка.querySelectorAll('.cmp-pick-cancelled').forEach((x) => x.remove());
    const строка = document.createElement('div');
    строка.className = 'cmp-pick-cancelled';
    строка.textContent = 'Выбор отменён — исследование не открыто. Нажмите «Открыть папку» ещё раз.';
    карточка.appendChild(строка);
  }
  log.info('[Сравнение] выбор каталога отменён, окно освобождено');
}

export function onStudiesChanged() {
  if (!isCompareActive()) return;

  // Исследование могло быть вытеснено — освобождаем окна, которые на него
  // ссылались, иначе врач увидит пустое окно без объяснения.
  const живые = new Set(_исследования().map((с) => с.studyUID));
  for (const [vpId, зап] of [..._вОкне]) {
    if (зап.studyUID && !живые.has(зап.studyUID)) _вОкне.delete(vpId);
  }

  const свободные = _свободные();
  const цель = _окноЖдёт || _окна.find((id) => !_вОкне.get(id)?.studyUID);
  if (цель && свободные.length) {
    _положить(цель, свободные[0].studyUID).catch((e) => log.warn('[Сравнение]', e.message));
    return;
  }
  _обновитьВсё();
}

// ── Состояние наружу ─────────────────────────────────────────────────

/**
 * Состояние сравнения одной строкой данных. Нужно не только проверкам: на
 * телефоне консоли нет, и когда режим поведёт себя странно, единственный
 * способ разобраться — спросить у программы, что она сама думает.
 */
export function getCompareStatus() {
  const состояние = {
    active: isCompareActive(), mode: _режимСвязи,
    alignment: getCompareAlignment(), windows: [],
  };
  try {
    const re = getRenderingEngine();
    for (const vpId of _окна) {
      const vp = re?.getViewport(vpId);
      const зап = _вОкне.get(vpId) || {};
      состояние.windows.push({
        vpId,
        studyUID: зап.studyUID || '',
        seriesUID: зап.seriesUID || '',
        index: vp?.getCurrentImageIdIndex?.() ?? null,
        total: vp?.getImageIds?.()?.length ?? 0,
        badge: vp?.element?.querySelector('.cmp-badge')?.textContent || '',
        ждётКаталог: _окноЖдёт === vpId,
      });
    }
  } catch (e) { log.debug('[Сравнение] состояние:', e.message); }
  return состояние;
}

let _привязано = false;

export function initComparePanel() {
  if (_привязано) return;
  _привязано = true;
  const кнопка = document.getElementById('btn-compare-mode');
  if (!кнопка) { log.debug('[Сравнение] кнопки нет в разметке'); return; }
  кнопка.addEventListener('click', () => {
    enterCompareMode().catch((e) => log.warn('[Сравнение] вход:', e.message));
  });
  log.debug('[Сравнение] кнопка готова');
}

registerAPIs({
  // Наружу — для проверок и для разбора, когда указатель поведёт себя странно.
  compareProbe: (откуда, x, y) => _показатьУказатель(откуда, { x, y }),
  compareMeasure: async (окно, x, y) => { _ждёмТочкуОчага = true; await _измеритьОчаг(окно, { x, y }); return _итогИзмерения; },
  compareLesionResult: getLesionResult,
  compareSeedRefine: (мм) => { try { localStorage.setItem('mikhmel.уточнятьСемяМм', String(мм)); } catch (e) { /* */ } },
  // [10.19.1] Что загружено прореженным — наружу для проверок и разбора.
  loadThinningInfo: () => thinningSnapshot(),
  compareStatus: getCompareStatus,
  enterCompareMode, exitCompareMode,
  compareMaxStudies: getMaxStudies,
  // [10.19.0] Выбор каталога отменён — окно должно сказать об этом, а не
  // молчать с той же карточкой. Зовётся из fileOps через общий реестр, чтобы
  // не заводить между ними прямой связи.
  compareOpenCancelled: onOpenCancelled,
  // [10.16.0] Карта разницы — наружу, чтобы её можно было проверить браузером
  // без нажатий мышью и разобрать, если цвет ляжет не туда.
  compareDiff: async (o) => (await import('../registration/differenceOverlay.js')).toggleDifference(o),
  compareDiffStatus: async () => (await import('../registration/differenceOverlay.js')).differenceStatus(),
  compareDiffRedraw: async () => (await import('../registration/differenceOverlay.js')).redrawDifference(),
});
